import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { LanguageToggle } from "@/components/LanguageToggle";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Target, Radar, Users, TrendingUp, CircleDot, ClipboardList } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1759733841123-b8e1d75ee45c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const features = [
  { icon: ClipboardList, title: "90-Second Logging", desc: "Batting, bowling & fielding with cricket-notation overs and live strike rate, economy, dot% as you type." },
  { icon: CircleDot, title: "Wagon Wheel", desc: "Tap-to-map every shot across 8 sectors, mirrored by batting hand, with zone analytics and dismissal overlays." },
  { icon: TrendingUp, title: "Analytics Dashboard", desc: "Form index, consistency score, trends, and practice-vs-tournament radar comparisons." },
  { icon: Users, title: "Coach Portal", desc: "Link a coach, share progress, and collect timestamped notes on every innings." },
  { icon: Target, title: "Goals & Reports", desc: "Season goals as progress bars with pace-to-target and auto report cards after every match." },
  { icon: Radar, title: "Private by Default", desc: "Your reflections and audio notes stay private. Share a public sports card only when you choose." },
];

export default function Landing() {
  const t = useT();
  return (
    <div className="min-h-screen bg-ink text-white grain">
      <header className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Logo light />
        <div className="flex items-center gap-2">
          <LanguageToggle dark />
          <Link to="/login"><Button variant="ghost" className="text-white hover:bg-white/10 hover:text-white" data-testid="landing-login-btn">{t("Log in")}</Button></Link>
          <Link to="/register"><Button className="bg-brand hover:bg-brand-dark" data-testid="landing-register-btn">{t("Get Started")}</Button></Link>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-10 pb-20 grid lg:grid-cols-2 gap-12 items-center">
        <div className="fade-up">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand/15 text-brand text-xs font-bold uppercase tracking-widest mb-6">
            <span className="w-2 h-2 rounded-full bg-brand" /> {t("Built for all budding cricket players")}
          </div>
          <h1 className="font-head font-black uppercase leading-[0.95] text-5xl sm:text-6xl lg:text-7xl tracking-tight">
            {t("Track every ball.")}<br /><span className="text-brand">{t("Own your game.")}</span>
          </h1>
          <p className="mt-6 text-slate-300 text-lg max-w-xl">
            {t("CrickTrack is the performance journal for grassroots and semi-pro cricketers. Log matches and nets, map your shots, and turn raw scores into sharp, coach-ready analytics.")}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/register"><Button size="lg" className="bg-brand hover:bg-brand-dark text-base h-12 px-8" data-testid="hero-cta-btn">{t("Start tracking free")}</Button></Link>
            <Link to="/login"><Button size="lg" variant="outline" className="border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white text-base h-12 px-8">{t("I have an account")}</Button></Link>
          </div>
        </div>
        <div className="relative fade-up">
          <div className="absolute -inset-4 bg-brand/20 blur-3xl rounded-full" />
          <img src={HERO} alt="Cricket stadium under lights" className="relative rounded-2xl border border-white/10 shadow-2xl object-cover w-full h-[380px]" />
        </div>
      </section>

      <section className="bg-white text-ink py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <p className="text-xs font-bold uppercase tracking-widest text-brand">{t("Everything a cricketer needs")}</p>
          <h2 className="font-head font-bold text-3xl sm:text-4xl mt-2 mb-10">{t("From scorebook to scouting report.")}</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {features.map((f) => (
              <div key={f.title} className="ct-card p-6 hover:border-brand transition-colors" data-testid={`feature-${f.title.split(" ")[0].toLowerCase()}`}>
                <div className="w-11 h-11 rounded-lg bg-ink text-brand flex items-center justify-center mb-4"><f.icon size={22} /></div>
                <h3 className="font-head font-bold text-xl mb-2">{t(f.title)}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{t(f.desc)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="py-10 text-center text-slate-500 text-sm">
        <Logo light /> <p className="mt-3">© 2026 CrickTrack. No video, ever — just the numbers that matter.</p>
      </footer>
    </div>
  );
}
